import React from 'react';
import { ReactComponent as NotificationBellIcon } from '../../../../assets/icons/notification-icons/notification-read-bell-icon.svg';
import { UserNotification, NotificationType } from '../../../../model/notifications/notifications';
import styles from './NotificationItem.module.scss';

interface NotificationItemProps {
    notification: UserNotification;
    onClick?: (notification: UserNotification) => void;
    isSelected?: boolean;
    compact?: boolean;
}

const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toLowerCase();
    return `${datePart}, ${timePart}`;
};

interface NotificationMeta {
    iconBg: string;
    iconColor: string;
    actionLabel?: string;
}

function getNotificationMeta(type: NotificationType): NotificationMeta {
    switch (type) {
        case NotificationType.BpsBilling:
            return { iconBg: 'rgba(243, 132, 21, 0.2)', iconColor: '#F38415', actionLabel: 'View' };
        case NotificationType.CustomerEnergyAccountPendingStatusChangedRequest:
            return { iconBg: 'rgba(24, 99, 228, 0.2)', iconColor: '#2773F6' };
        case NotificationType.UnitRunningLow:
            return { iconBg: 'rgba(243, 132, 21, 0.2)', iconColor: '#F38415', actionLabel: 'View' };
        case NotificationType.ServiceRequest:
        case NotificationType.ServiceRequestPayment:
            return { iconBg: 'rgba(24, 99, 228, 0.2)', iconColor: '#2773F6', actionLabel: 'View' };
        case NotificationType.VendingToken:
        case NotificationType.BorrowToken:
            return { iconBg: 'rgba(243, 132, 21, 0.2)', iconColor: '#F38415', actionLabel: 'View' };
        case NotificationType.WalletCredited:
        case NotificationType.WalletDebited:
            return { iconBg: 'rgba(24, 99, 228, 0.2)', iconColor: '#2773F6', actionLabel: 'View' };
        case NotificationType.Refund:
            return { iconBg: 'rgba(243, 132, 21, 0.2)', iconColor: '#F38415', actionLabel: 'View' };
        case NotificationType.SolarRecommendation:
            return { iconBg: 'rgba(52, 184, 87, 0.2)', iconColor: '#34B857', actionLabel: 'View' };
        default:
            return { iconBg: 'rgba(243, 132, 21, 0.2)', iconColor: '#F38415', actionLabel: 'View' };
    }
}


const NotificationItem: React.FC<NotificationItemProps> = ({
    notification,
    onClick,
    isSelected,
    compact,
}) => {
    const isUnread = !notification.readAt;
    const meta = getNotificationMeta(notification.type);

    return (
        <div
            className={`${styles.item} ${isUnread ? styles.unread : styles.read} ${isSelected ? styles.selected : ''} ${compact ? styles.compact : ''}`}
            onClick={() => onClick?.(notification)}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick(notification) : undefined}
        >
            <div className={styles.row}>
                <div
                    className={styles.iconCircle}
                    style={{ backgroundColor: meta.iconBg }}
                >
                    <NotificationBellIcon style={{ color: meta.iconColor }} />
                </div>
                <div className={styles.content}>
                    <div className={styles.titleRow}>
                        <span className={styles.title}>{notification.title}</span>
                        {isUnread && <span className={styles.unreadDot} />}
                    </div>
                    <p className={styles.message}>{notification.message}</p>
                    <span className={styles.date}>{formatDateTime(notification.createdAt)}</span>
                    {meta.actionLabel && (
                        <button
                            className={styles.actionBtn}
                            onClick={e => { e.stopPropagation(); onClick?.(notification); }}
                        >
                            {meta.actionLabel}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationItem;
